import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  UserIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { LeagueService, SeasonService } from '../../../services/firebaseService';
import SeasonTeamsList from '../../../components/shared/SeasonTeamsList';

export default function SeasonPlayers() {
  const { leagueId, seasonId } = useParams();
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId && seasonId) {
      loadSeasonData();
    }
  }, [leagueId, seasonId]);

  const loadSeasonData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [leagueData, seasonData] = await Promise.all([
        LeagueService.getLeague(leagueId),
        SeasonService.getSeason(seasonId)
      ]);

      if (!leagueData) {
        throw new Error('League not found');
      }

      if (!seasonData) {
        throw new Error('Season not found');
      }
      
      setLeague(leagueData);
      setSeason(seasonData);
    } catch (error) {
      console.error('Error loading season data:', error);
      setError(error.message || 'Failed to load season data. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading season players...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Season</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={loadSeasonData}
              className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
            >
              Try Again
            </button>
            <Link
              to={`/admin/leagues/${leagueId}`}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Back to League
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!league || !season) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Season Not Found</h2>
          <p className="text-gray-600 mb-4">The requested season could not be found.</p>
          <Link
            to={`/admin/leagues/${leagueId}`}
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Back to League
          </Link>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center">
            <Link
              to={`/admin/leagues/${leagueId}`}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div className="flex items-center">
              <TrophyIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{season.name}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {league.name}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Season Stats */}
        <div className="px-4 py-6 sm:px-0 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrophyIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Contestants
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {season.contestants?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <StarIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Weeks
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {season.weeks?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Teams Leaderboard */}
        <div className="px-4 py-6 sm:px-0">
          <SeasonTeamsList
            leagueId={leagueId}
            seasonId={seasonId}
            season={season}
            league={league}
            showActions={false}
            showTeamLinks={true}
          />
        </div>
      </div>
    </div>
  );
}
